import React, { useState } from 'react';
import { AlertCircle, CheckCircle, CreditCard, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import api from '@/lib/axios';
import * as stripeService from '@/services/stripeService';

interface ExpiredSubscriptionOverlayProps {
  onClose: () => void;
  user?: { name: string; email: string } | null;
  store?: { _id: string; name: string; storeNumber: string } | null;
}

const ExpiredSubscriptionOverlay: React.FC<ExpiredSubscriptionOverlayProps> = ({ onClose: _onClose, user, store }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [redirectingToStripe, setRedirectingToStripe] = useState(false);
  const { toast } = useToast();

  const [requestSent, setRequestSent] = useState(false);

  const handleSubscribeNow = async () => {
    try {
      setRedirectingToStripe(true);

      // Create Stripe Checkout session with all features enabled (default for expired subscriptions)
      const allFeatures = {
        fohTasks: true,
        setups: true,
        kitchen: true,
        documentation: true,
        training: true,
        evaluations: true,
        leadership: true,
        leadershipPlans: true
      };

      const session = await stripeService.createCheckoutSession(allFeatures, 'monthly');

      // Redirect to Stripe Checkout
      window.location.href = session.url;
    } catch (error: any) {

      toast({
        title: 'Error',
        description: 'Failed to start checkout. Please try again or contact support.',
        variant: 'destructive'
      });

      setRedirectingToStripe(false);
    }
  };

  const handleRequestReactivation = async () => {
    try {
      setIsSubmitting(true);

      // Collect whatever information we have
      const requestData = {
        storeId: store?._id || '',
        storeName: store?.name || '',
        storeNumber: store?.storeNumber || '',
        userName: user?.name || '',
        userEmail: user?.email || ''
      };
      // Send email to Jonathon
      await api.post('/api/admin/request-reactivation', requestData);

      toast({
        title: 'Request Sent',
        description: 'Your reactivation request has been sent. We will contact you shortly.',
        variant: 'default'
      });

      // Mark as sent but don't close the overlay
      setRequestSent(true);
    } catch (error: any) {
      const _castErr = error as { response?: { status?: number; data?: { message?: string; [k: string]: unknown }; }; message?: string; code?: string; config?: any; };
      console.error('Error sending reactivation request:', error);

      // Extract error message if available
      const errorMessage = _castErr.response?.data?.message ||
                          _castErr.message ||
                          'Failed to send reactivation request';

      toast({
        title: 'Error',
        description: `${errorMessage}. Please try again or contact support directly at Jonathon@ld-growth.com.`,
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="relative w-full max-w-md p-6 bg-white rounded-lg shadow-xl border-2 border-red-500">

        <div className="flex flex-col items-center text-center">
          {!requestSent ? (
            <>
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-2">Subscription Expired</h2>

              <p className="text-gray-700 mb-6">
                Your subscription has expired. All features are currently disabled.
                You must request reactivation to restore access to the system.
              </p>

              <p className="text-sm text-gray-600 mb-6">
                Choose an option below to restore access to your account.
              </p>

              <div className="w-full space-y-3">
                {/* Primary: Subscribe Now via Stripe */}
                <Button
                  variant="default"
                  className="w-full bg-[#E51636] text-white hover:bg-[#C41230] hover:text-white font-bold py-3 text-lg"
                  onClick={handleSubscribeNow}
                  disabled={redirectingToStripe || isSubmitting}
                >
                  {redirectingToStripe ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Redirecting to Checkout...
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-5 w-5 mr-2" />
                      Subscribe Now
                    </>
                  )}
                </Button>

                {/* Secondary: Request Reactivation */}
                <Button
                  variant="outline"
                  className="w-full border-gray-300 hover:bg-gray-50 font-semibold py-3"
                  onClick={handleRequestReactivation}
                  disabled={isSubmitting || redirectingToStripe}
                >
                  {isSubmitting ? 'Sending Request...' : 'Request Manual Reactivation'}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  Subscribe now for instant access, or request manual reactivation if you need assistance.
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-2">Request Sent</h2>

              <p className="text-gray-700 mb-6">
                Your reactivation request has been sent to the system administrator.
                We will contact you shortly to restore your access.
              </p>

              <p className="text-sm text-gray-600 mb-6">
                You may close this window, but you will need to wait for your subscription
                to be reactivated before you can use the system again.
              </p>

              <p className="text-sm text-gray-500 mb-6">
                If you don&apos;t hear back within 24 hours, please contact support at:
                <br />
                <a href="mailto:jonp4208@gmail.com" className="text-blue-600 hover:underline">
                  jonp4208@gmail.com
                </a>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpiredSubscriptionOverlay;
